import { useEffect, useState } from "react";
import { Hand, UserCheck, XCircle, Trash2, Archive, FileDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { formatDate, formatDateTime } from "@/constants";

const STATUS = {
  aide: { label: "Vient aider", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100" },
  participe: { label: "Bénéficiaire", cls: "bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-100" },
  absent: { label: "Absent", cls: "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100" },
};

export default function AdminRsvps() {
  const [groups, setGroups] = useState([]);
  const [participations, setParticipations] = useState([]);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [motifTarget, setMotifTarget] = useState(null);
  const [motif, setMotif] = useState("");
  const [archiving, setArchiving] = useState(false);

  const refreshParticipations = () => {
    api.get("/admin/event-participations").then((r) => setParticipations(r.data)).catch(() => {});
    api.get("/admin/participations-archive").then((r) => setArchives(r.data)).catch(() => {});
  };

  useEffect(() => {
    let done = 0;
    const tick = () => { done += 1; if (done >= 3) setLoading(false); };
    api.get("/admin/rsvps").then((r) => setGroups(r.data)).catch(() => {}).finally(tick);
    api.get("/admin/event-participations").then((r) => setParticipations(r.data)).catch(() => {}).finally(tick);
    api.get("/admin/participations-archive").then((r) => setArchives(r.data)).catch(() => {}).finally(tick);
  }, []);

  const archive = async () => {
    if (!motifTarget || motif.trim().length < 2) return;
    setArchiving(true);
    try {
      await api.post(`/admin/event-participations/${motifTarget.id}/archive`, { motif: motif.trim() });
      toast.success("Participation supprimée et archivée avec motif.");
      setMotifTarget(null);
      setMotif("");
      refreshParticipations();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-rsvps-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="admin-rsvps-title">Participations des adhérents</h1>
        <p className="text-sm text-slate-500 mt-1">Réponses des adhérents aux événements : aide, participation, absence et observations.</p>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Chargement…</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-6" data-testid="rsvps-empty">
          Aucune participation enregistrée pour le moment. Les adhérents peuvent répondre depuis leur espace, onglet Événements.
        </p>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <Card key={g.event_id} className="border-slate-200" data-testid={`rsvp-event-${g.event_id}`}>
              <CardContent className="p-5 space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-semibold text-slate-800">{g.event_title}</p>
                    <p className="text-xs text-slate-500">{formatDate(g.event_date)}</p>
                  </div>
                  <div className="flex gap-2">
                    <Badge className={STATUS.aide.cls}><Hand className="h-3 w-3 mr-1" />{g.aide}</Badge>
                    <Badge className={STATUS.participe.cls}><UserCheck className="h-3 w-3 mr-1" />{g.participe}</Badge>
                    <Badge className={STATUS.absent.cls}><XCircle className="h-3 w-3 mr-1" />{g.absent}</Badge>
                  </div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {g.responses.map((r, i) => (
                    <li key={i} className="py-2.5 flex flex-wrap items-center gap-3" data-testid={`rsvp-row-${g.event_id}-${i}`}>
                      <span className="text-sm font-medium text-slate-700 min-w-[140px]">{r.user_name}</span>
                      <Badge className={STATUS[r.status].cls}>{STATUS[r.status].label}</Badge>
                      {r.note && <span className="text-sm text-slate-500 italic">« {r.note} »</span>}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="pt-4 border-t border-slate-200">
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900" data-testid="participations-title">
          Inscriptions « Je participe » (formulaire public)
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Familles inscrites via le bandeau ou le calendrier. Le n° d'adhérent est indiqué quand la famille l'a renseigné.
          Une suppression demande un motif et archive la fiche.
        </p>

        {participations.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-6" data-testid="participations-empty">
            Aucune inscription pour le moment.
          </p>
        ) : (
          <div className="mt-4 space-y-3" data-testid="participations-list">
            {participations.map((p) => (
              <Card key={p.id} className="border-slate-200" data-testid={`participation-${p.id}`}>
                <CardContent className="p-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                  <div className="min-w-[200px] flex-1">
                    <p className="font-semibold text-slate-800 text-sm">{p.adults}</p>
                    <p className="text-xs text-slate-500">
                      {p.event_title} · {p.children_count} enfant(s) : {p.children_names}
                    </p>
                    <p className="text-xs text-slate-500">{p.phone} · {p.email}</p>
                  </div>
                  <Badge className="bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-100" data-testid={`participation-number-${p.id}`}>
                    N° adhérent : {p.membership_number || "—"}
                  </Badge>
                  {p.member_status && (
                    <Badge className={p.member_status === "non_adherent"
                      ? "bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100"
                      : "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"}
                      data-testid={`participation-quality-${p.id}`}>
                      {p.member_status === "non_adherent" ? "Pas encore adhérent" : "Déjà adhérent"}
                    </Badge>
                  )}
                  {p.image_consent && (
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">Droit à l'image OK</Badge>
                  )}
                  <span className="text-xs text-slate-400">{formatDateTime(p.created_at)}</span>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => { setMotifTarget(p); setMotif(""); }} data-testid={`participation-delete-${p.id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />Supprimer
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {archives.length > 0 && (
        <div className="pt-4 border-t border-slate-200" data-testid="participations-archive-section">
          <h2 className="text-lg font-bold tracking-tight text-slate-700 flex items-center gap-2">
            <Archive className="h-5 w-5 text-slate-400" />Participations supprimées (archive) — classées par événement
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Chaque suppression est conservée avec toutes les informations renseignées, le motif et l'auteur. Export PDF par événement, à volonté.
          </p>
          <div className="mt-4 space-y-5">
            {Object.entries(archives.reduce((acc, a) => {
              (acc[a.event_id || a.event_title] = acc[a.event_id || a.event_title] || []).push(a);
              return acc;
            }, {})).map(([eventKey, items]) => (
              <Card key={eventKey} className="border-slate-200" data-testid={`archive-event-${eventKey}`}>
                <CardContent className="p-5 space-y-3">
                  <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{items[0].event_title}</p>
                      <p className="text-xs text-slate-500">{items.length} participation(s) archivée(s)</p>
                    </div>
                    <Button variant="outline" size="sm" className="text-slate-700 border-slate-300 hover:bg-slate-50"
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/participations-archive/export/pdf?event_id=${items[0].event_id}`, "_blank")}
                      data-testid={`archive-pdf-${eventKey}`}>
                      <FileDown className="h-3.5 w-3.5 mr-1.5" />Export PDF (archive)
                    </Button>
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-xs" data-testid={`archive-table-${eventKey}`}>
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 text-left">
                          <th className="px-3 py-2 font-semibold">Adulte(s)</th>
                          <th className="px-3 py-2 font-semibold">Enfant(s)</th>
                          <th className="px-3 py-2 font-semibold">Téléphone</th>
                          <th className="px-3 py-2 font-semibold">Email</th>
                          <th className="px-3 py-2 font-semibold">N° adhérent</th>
                          <th className="px-3 py-2 font-semibold">Supprimée le</th>
                          <th className="px-3 py-2 font-semibold">Par</th>
                          <th className="px-3 py-2 font-semibold">Motif</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((a, i) => (
                          <tr key={a.id || i} className="text-slate-600" data-testid={`archive-row-${eventKey}-${i}`}>
                            <td className="px-3 py-2 font-medium text-slate-700">{a.adults}</td>
                            <td className="px-3 py-2">{a.children_count} — {a.children_names || "—"}</td>
                            <td className="px-3 py-2">{a.phone}</td>
                            <td className="px-3 py-2">{a.email}</td>
                            <td className="px-3 py-2">{a.membership_number || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(a.archived_at)}</td>
                            <td className="px-3 py-2">{a.archived_by}</td>
                            <td className="px-3 py-2 italic">{a.motif}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!motifTarget} onOpenChange={(o) => { if (!o) setMotifTarget(null); }}>
        <DialogContent data-testid="participation-archive-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer cette participation</DialogTitle>
          </DialogHeader>
          {motifTarget && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                <strong>{motifTarget.adults}</strong> — {motifTarget.event_title}. La fiche sera archivée avec le motif
                (traçabilité) et retirée de la liste.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="archive-motif">Motif de la suppression *</Label>
                <Input id="archive-motif" value={motif} onChange={(e) => setMotif(e.target.value)}
                  placeholder="Ex. : demande de la famille, doublon, annulation…" data-testid="archive-motif-input" />
              </div>
              <div className="flex gap-2.5">
                <Button className="bg-red-600 hover:bg-red-700" onClick={archive}
                  disabled={archiving || motif.trim().length < 2} data-testid="archive-confirm-btn">
                  {archiving ? "Suppression…" : "Supprimer avec ce motif"}
                </Button>
                <Button variant="outline" onClick={() => setMotifTarget(null)}>Annuler</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
